import chroma from "chroma-js";
import { useTranslation } from "../../../localization";
import { Slider } from "../../../slider";
import { cn } from "../../../utils";

/** Props Interface */
interface AnnotationColorSwatchProps {
	value: string;
	onChange: (value: string) => void;
	className?: string;
	withAlpha?: boolean;
}

/**
 * Parses any CSS color string (named, hex, rgb/rgba) into a `#rrggbb` hex and its alpha.
 * Falls back to opaque black on invalid input, since the native `<input type="color">`
 * only accepts `#rrggbb`.
 */
function parseColor(value: string): { hex: string; alpha: number } {
	try {
		const color = chroma(value);
		return { hex: color.hex("rgb"), alpha: color.alpha() };
	} catch {
		return { hex: "#000000", alpha: 1 };
	}
}

export function AnnotationColorSwatch({
	value,
	onChange,
	className,
	withAlpha = true,
}: AnnotationColorSwatchProps) {
	/** Hooks */
	const { t } = useTranslation();

	/** Computed */
	const { hex, alpha } = parseColor(value);

	/** Functions */
	function emit(nextHex: string, nextAlpha: number) {
		if (!withAlpha || nextAlpha >= 1) {
			onChange(nextHex);
			return;
		}
		onChange(chroma(nextHex).alpha(nextAlpha).css());
	}

	/** Render */
	return (
		<div className="flex flex-col gap-2">
			<input
				type="color"
				value={hex}
				onChange={(e) => emit(e.target.value, alpha)}
				className={cn(
					"h-8 w-full shrink-0 cursor-pointer appearance-none rounded-full border border-primary-700 bg-transparent p-0",
					"[&::-webkit-color-swatch-wrapper]:p-0 [&::-webkit-color-swatch]:rounded-full [&::-webkit-color-swatch]:border-none",
					"[&::-moz-color-swatch]:rounded-full [&::-moz-color-swatch]:border-none",
					className,
				)}
			/>
			{withAlpha && (
				<div className="flex flex-col gap-1">
					<div className="flex flex-row justify-between items-center **:text-white! text-xs">
						<p>{t("image_annotator.opacity")}</p>
						<p>{Math.round(alpha * 100)}%</p>
					</div>
					<Slider
						className="w-full"
						min={0}
						max={100}
						value={[Math.round(alpha * 100)]}
						onChange={([next]) => emit(hex, (next ?? 100) / 100)}
					/>
				</div>
			)}
		</div>
	);
}
